import type { MaybeRefOrGetter } from '#imports'
import { toValue, useCoreConfig, useGameConfigurationClient, useQuery, useUserInfo } from '#imports'
import type { components } from '@core/types/clients/game-configuration'

export type GameSection = {
    name: string
    games: components['schemas']['PublicGameConfigurationRepresentation'][]
    type: 'Horizontal' | 'Vertical'
    horizontalLines: number | null
}

export function useGameSection(options: { slug: MaybeRefOrGetter<string>, page?: number, page_size?: number, enabled?: MaybeRefOrGetter<boolean>, lazy: boolean, server?: boolean }) {
    const { client } = useGameConfigurationClient()
    const { site } = useCoreConfig()
    const { isStaff } = useUserInfo()

    const { enabled = true, lazy = true, page = 1, page_size = null, slug, server = true } = options

    return useQuery<GameSection>(async () => {
        const slugValue = toValue(slug)
        const { data } = await client.GET('/public/game-configurations/list', {
            params: {
                query: {
                    brand: site.brand,
                    slug: slugValue,
                    page,
                    ...(page_size && { page_size }),
                    ...(isStaff.value && { is_staff: true }),
                },
            },
        })

        const games = [...(data?.games ?? [])].sort(
            (a, b) => (a.sort_index ?? 0) - (b.sort_index ?? 0),
        )

        return {
            name: data?.name || slugValue,
            games,
            type: data?.type ?? 'Horizontal',
            horizontalLines: data?.type === 'Horizontal' && data?.horizontal_lines ? data.horizontal_lines : null
        }
    }, {
        enabled,
        lazy,
        server,
        key: () => `game:game-section:${toValue(slug)}`,
    })
}
